import type { CSSProperties, ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * The section label: Geist Mono, uppercase, 0.18em tracking.
 *
 * Wraps the existing `.instrument-label` class in globals.css rather than
 * re-declaring the type spec — that class is deliberately unlayered so a bare
 * `p` rule cannot outrank it, and duplicating the declarations here would
 * fork the definition.
 *
 * Marketing eyebrows are brand magenta by default (2026-08-08). The Radar
 * dashboard passes tone='secondary' / 'muted' explicitly, because magenta
 * carries "act now / Grade F" meaning on that surface and must stay a
 * signal there rather than decoration.
 *
 * The leading swatch tick was removed in the same pass — the eyebrow reads
 * as a label on its own, and the tick had become a second accent competing
 * with the text colour.
 *
 * ALWAYS a <span>. These sit in `flex items-center` rows next to dots and
 * icons, where an h1-h6 margin breaks vertical alignment (CLAUDE.md §7).
 */
export function InstrumentLabel({
  children,
  /** Trailing content — a count, a chip. */
  trailing,
  /** Tone of the label text. Defaults to the brand magenta eyebrow. */
  tone = 'accent',
  className,
  style,
}: {
  children: ReactNode
  trailing?: ReactNode
  tone?: 'accent' | 'secondary' | 'muted' | 'inherit'
  className?: string
  /** For a per-instance accent colour; pair with tone='inherit'. */
  style?: CSSProperties
}) {
  return (
    <span
      className={cn(
        'instrument-label flex items-center gap-2 leading-none',
        tone === 'accent' && 'text-cta',
        tone === 'secondary' && 'text-muted-foreground',
        tone === 'muted' && 'text-instrument',
        className
      )}
      style={style}
    >
      {children}
      {trailing}
    </span>
  )
}
